<template>
    <base-modal v-if="show" size="xl" hide-footer hide-header prevent-close no-padding>
        <div style="position: relative; height: 60vh; padding: 1em;">
            <leaflet-map ref="map" @mapReady="onMapReady" />
        </div>
        <hr class="mx-2">
        <div style="height: 150px; overflow-y: hidden; font-size: 0.85em;">
            <div class="d-flex px-3 pb-2 h-100">
                <div class="d-flex flex-column" style="flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden">
                    <div style="flex: 1;">
                        <div class="row">
                            <div class="col">
                                <div v-if="geometries && geometries.length">
                                    <strong>{{ $t('orders.geometry') }} ({{ this.geometries?.length }})</strong>
                                    <div class="mt-2 mb-2">
                                        <button class="btn btn-sm btn-outline-primary" @click.stop="allGeometriesSelected = !allGeometriesSelected">
                                            {{ allGeometriesSelected ? $t('route_optimization.deselect_all') : $t('route_optimization.select_all')}}
                                        </button>
                                    </div>

                                    <div v-for="(geom, i) in geometries" :key="i" class="mt-1 d-flex align-items-center">
                                        <input
                                            type="checkbox"
                                            :id="'geom-' + i"
                                            v-model="visibleLayers['geom-' + i]"
                                            @change="toggleLayer('geom-' + i)"
                                            class="me-2"
                                        >
                                        <label :for="'geom-' + i" class="mb-0 d-flex align-items-center" style="cursor: pointer">
                                            <span :style="{color: geom.color || '#B200FF', fontSize: '1.2em'}">●</span>
                                            <span class="ms-2">{{ geom.name }}</span>
                                            <span v-if="geom.geometry" class="ms-2 text-muted">({{ getGeometryTypeLabel(geom.geometry) }})</span>
                                        </label>
                                    </div>
                                </div>
                                <div v-else-if="geometries === null">
                                    <strong>{{ $t('orders.geometry') }}</strong>
                                    <div class="ms-3 mt-1 text-muted">{{ $t('orders.no_geometry') }}</div>
                                </div>
                            </div>
                            <div class="col">
                                <div v-if="completed || incompleted || workPolygons" style="flex: 1;">
                                    <strong>{{ $t('menu.fleet_trips') }}</strong>
                                    <div v-if="completed" class="mt-1 d-flex align-items-center">
                                        <input type="checkbox" id="completed" v-model="visibleLayers.completed" @change="toggleLayer('completed')" class="me-2">
                                        <label for="completed" class="mb-0 d-flex align-items-center" style="cursor: pointer">
                                            <span style="color: #00FF00; fontSize: 1.2em">●</span>
                                            <span class="ms-2">{{ $t('orders.completed') }}</span>
                                            <span class="ms-2 text-muted">({{ getFeatureCountLabel(completed) }})</span>
                                        </label>
                                    </div>
                                    <div v-if="incompleted" class="mt-1 d-flex align-items-center">
                                        <input type="checkbox" id="incompleted" v-model="visibleLayers.incompleted" @change="toggleLayer('incompleted')" class="me-2">
                                        <label for="incompleted" class="mb-0 d-flex align-items-center" style="cursor: pointer">
                                            <span style="color: #FF0000; fontSize: 1.2em">●</span>
                                            <span class="ms-2">{{ $t('orders.incompleted') }}</span>
                                            <span class="ms-2 text-muted">({{ getFeatureCountLabel(incompleted) }})</span>
                                        </label>
                                    </div>
                                    <div v-if="workPolygons" class="mt-1 d-flex align-items-center">
                                        <input type="checkbox" id="workPolygons" v-model="visibleLayers.workPolygons" @change="toggleLayer('workPolygons')" class="me-2">
                                        <label for="workPolygons" class="mb-0 d-flex align-items-center" style="cursor: pointer">
                                            <span style="color: #0000FF; fontSize: 1.2em">●</span>
                                            <span class="ms-2">{{ $t('orders.work_polygons') }}</span>
                                            <span class="ms-2 text-muted">({{ getFeatureCountLabel(workPolygons) }})</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div style="width: 400px; margin-left: 1em; border-left: 1px solid #ddd; padding-left: 1em;">
                    <div class="d-flex align-items-center mb-1">
                        <div class="form-check form-switch">
                            <input class="form-check-input" type="checkbox" id="allGeometriesReady" v-model="allGeometriesReady" @change="onAllGeometriesReadyChange" :disabled="loading">
                            <label class="form-check-label me-2" for="allGeometriesReady">{{ $t('orders.all_geometries_ready') }}</label>
                        </div>
                    </div>
                    <div class="d-flex align-items-center mb-1">
                        <div class="form-check form-switch">
                            <input class="form-check-input" type="checkbox" id="orderCompleted" v-model="orderCompleted" @change="onAllGeometriesReadyChange" :disabled="loading">
                            <label class="form-check-label me-2" for="orderCompleted">{{ $t('orders.complete_order') }}</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <hr class="mx-2">
        <div class="col-12 d-flex justify-content-end pb-2 pe-3">
            <button class="btn btn-secondary form-button" @click="$emit('close')">{{ $t('common.close') }}</button>
            <button :disabled="!coverageData" class="btn btn-outline-secondary form-button" @click="showJsonModal = true">{{ $t('orders.show_json') }}</button>
            <button class="btn btn-success form-button me-2" @click="sendOrder" :disabled="loading">{{ $t('common.send') }}</button>
        </div>
        <base-modal v-if="showJsonModal" size="lg" title="Coverage Data JSON" @close="showJsonModal = false">
            <pre style="font-size: 0.85em; white-space: pre-wrap; word-wrap: break-word; background: #f8f9fa; padding: 1em; border-radius: 4px; max-height: 60vh; overflow-y: auto;">{{ JSON.stringify(coverageData, null, 2) }}</pre>
            <template #footer>
                <button class="btn btn-secondary form-button" @click="showJsonModal = false">{{ $t('common.close') }}</button>
            </template>
        </base-modal>
        <div v-if="loading || submitting" id="loader" class="spinner" />
        <Transition name="fade">
            <div v-if="loading" id="loader" class="spinner-text">{{ $t('orders.completion_data_loading')}}</div>
        </Transition>
    </base-modal>
</template>

<script>
import BaseModal from '@/components/BaseModal.vue'
import LeafletMap from '@/components/map/LeafletMap.vue'
import { restApi } from '../mixins/RestApiMixin'
import { geometryMixin } from '../mixins/GeometryMixin'
import {inject} from "vue";

export default {
    name: 'OrderCompletionMapModal',
    components: { BaseModal, LeafletMap },
    mixins: [restApi, geometryMixin],
    props: {
        show: { type: Boolean, default: false },
        orderId: { type: Number, required: true },
        geometries: { type: Array, default: null }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    emits: ['close'],
    data() {
        return {
            visibleLayers: {},
            layerRefs: {},
            showJsonModal: false,
            allGeometriesReady: false,
            orderCompleted: false,
            completed: null,
            incompleted: null,
            workPolygons: null,
            coverageData: null,
            loading: false,
            submitting: false,
            allGeometriesSelected: true
        }
    },
    mounted() {
        if (this.show) {
            this.fetchCompletionData()
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.fetchCompletionData()
            }
        },
        allGeometriesSelected() {
            this.toggleAllGeometries()
        }
    },
    methods: {
        toggleAllGeometries(event) {
            const visible = this.allGeometriesSelected
            this.geometries.forEach((geom, i) => {
                const key = 'geom-' + i
                this.visibleLayers[key] = visible
                const layerIndex = this.layerRefs[key]
                if (layerIndex !== undefined) {
                    this.$refs.map.setGeoJsonVisibility(
                        layerIndex,
                        visible,
                        false
                    )
                }
            })
            if (visible) {
                this.$refs.map.redrawGeoJsonLayers()
            }
        },
        showOrderGeometries() {
            if (this.geometries) {
                this.geometries.forEach((geom, i) => {
                    const key = 'geom-' + i
                    this.$refs.map.addGeoJsonLayer(geom.geometry, false, geom.color || '#B200FF', false, 1, 1, true)
                    this.visibleLayers[key] = true
                    this.layerRefs[key] = this.$refs.map.geoJsonLayers.length - 1
                })
                this.$refs.map.zoomToGeoJsons()
            }

        },
        onMapReady() {
            // Map is ready, check if we already have data to display
            if (this.completed || this.incompleted || this.workPolygons) {
                this.initializeMap({
                    completed: this.completed,
                    incompleted: this.incompleted,
                    work_polygons: this.workPolygons
                })
            } else {
                this.showOrderGeometries()
            }
        },
        toggleLayer(key) {
            const layerIndex = this.layerRefs[key]
            if (layerIndex !== undefined && this.$refs.map) {
                this.$refs.map.toggleGeoJsonVisibility(layerIndex)
            }
        },
        sendOrder() {
            this.submitting= true
            const params = {}
            params.all_geometries = this.allGeometriesReady ? "1" : "0"
            params.order_completed = this.orderCompleted ? "1" : "0"
            this.restAdd(this.orderUrl + '/' + this.orderId + '/send', params, this.success, this.fail)
        },
        onAllGeometriesReadyChange() {
            this.fetchCompletionData()
        },
        fetchCompletionData() {
            if (this.allGeometriesReady) {
                this.submitting = true
            } else {
                this.loading = true
            }
            this.restFetchParams(this.orderUrl + '/' + this.orderId + '/coverage',
                { all_geometries: this.allGeometriesReady ? "1" : "0" },
                this.handleCompletionData,
                (error) => {
                    this.loading = false
                    this.submitting = false
                    this.showAlert(this.$t('orders.completion_data_error'))
                })
        },
        handleCompletionData(response) {
            this.loading = false
            this.submitting = false
            if (response && response.data) {
                this.completed = response.data.completed
                this.incompleted = response.data.incompleted
                this.workPolygons = response.data.work_polygons
                this.coverageData = response.data
                this.clearMap()
                setTimeout(() => {
                    this.initializeMap(response.data)
                }, 100)
            }
        },
        clearMap() {
            if (this.$refs.map && typeof this.$refs.map.removeGeoJsonLayers === 'function') {
                this.$refs.map.removeGeoJsonLayers()
            }
            this.visibleLayers = {}
            this.layerRefs = {}
        },
        initializeMap(data) {
            if (!this.$refs.map) {
                return
            }

            if (data.completed) {
                this.$refs.map.addGeoJsonLayer(data.completed, false, '#00FF00', false, 0.5, 2, true)
                this.visibleLayers.completed = true
                this.layerRefs.completed = this.$refs.map.geoJsonLayers.length - 1
            }
            if (data.incompleted) {
                this.$refs.map.addGeoJsonLayer(data.incompleted, false, '#FF0000', false, 0.5, 2, true)
                this.visibleLayers.incompleted = true
                this.layerRefs.incompleted = this.$refs.map.geoJsonLayers.length - 1
            }
            if (data.work_polygons) {
                this.$refs.map.addGeoJsonLayer(data.work_polygons, false, '#0000FF', false, 0.5, 2, true)
                this.visibleLayers.workPolygons = true
                this.layerRefs.workPolygons = this.$refs.map.geoJsonLayers.length - 1
            }
            this.showOrderGeometries()
            if (data.completed || data.incompleted || data.work_polygons || this.geometries) {
                this.$refs.map.zoomToGeoJsons()
            }
        },
        success() {
            this.loading = false
            this.submitting = false
            this.showAlert(this.$t('orders.send_success_tiera'), 'success')
        },
        fail() {
            this.loading = false
            this.submitting = false
            this.showAlert(this.$t('orders.send_failed_tiera'))
        }
    }
}
</script>

<style scoped>
.leaflet-container {
    height: 100%;
    width: 100%;
}
</style>
